// LabelChip — small colored pill used in the TableView "Labels" column.
//
// Display only — editing labels happens inside TaskModal (owned by another
// agent). Color is a hex value coming from the server's labels endpoint;
// we render with that as background and pick a foreground color that has
// enough contrast.

interface Props {
  name: string;
  color: string;
  /** Render an even smaller version (used inside the row cell). */
  compact?: boolean;
  title?: string;
}

/**
 * Pick black or white text depending on the perceived luminance of the
 * background hex color. Exported for every surface that paints text on an
 * arbitrary label color.
 *
 * NON-HEX INPUT: some callers pass a CSS custom property (e.g.
 * `project.color ?? "var(--color-primary)"`) instead of a real label hex —
 * that string can't be parsed for luminance. Falling back to a hardcoded
 * "#fff" here used to be silently wrong: in dark mode `--color-primary` is a
 * LIGHT blue (#6293ff), so white text on it lands around 2.3:1 contrast,
 * under the 4.5:1 AA floor (the same failure class g2-admin fixed at 28
 * `text-white` call sites — just arriving here as a quiet default instead of
 * a literal class). `--color-on-accent` is themed for exactly this pairing
 * (accent-ish surface, readable in both light and dark), so unparseable
 * input resolves to that instead of a hardcoded color. Do not "simplify"
 * this back to a plain white/black fallback.
 */
export function pickFg(hex: string): string {
  const trimmed = hex.trim();
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(trimmed);
  if (!m) return "var(--color-on-accent)";
  let h = m[1];
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Rec. 601 luma — cheap and good enough for our brand palette.
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6 ? "#1a1a1a" : "#ffffff";
}

export function LabelChip(props: Props): preact.JSX.Element {
  const { name, color, compact = false, title } = props;
  const fg = pickFg(color);
  const cls = compact
    ? "inline-flex items-center max-w-full truncate rounded px-1.5 py-0 text-label font-medium leading-4"
    : "inline-flex items-center max-w-full truncate rounded px-2 py-0.5 text-label font-medium leading-4";
  return (
    <span
      class={cls}
      style={{ background: color, color: fg }}
      title={title ?? name}
    >
      <span class="truncate">{name}</span>
    </span>
  );
}
